import { useEffect } from "react";
import {
  AppShellTrigger,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  Button,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  Kbd,
  KbdGroup,
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuShortcut,
  MenuTrigger,
  Separator,
  Tabs,
  TabsList,
  TabsTab,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useShellTier,
} from "@salestrak/ui";

import { FilterToggle } from "#app/components/filters/filter-toggle";
import {
  INBOX_FILTERS,
  inboxFilterLabel,
  isInboxFilterAvailable,
  type InboxFilter,
  type InboxOrdering,
} from "#app/hooks/inbox/inbox-filters";
import type { InboxTrigger } from "./inbox-notification-item";

interface InboxHeaderProps {
  activeTab: "all" | "cleared";
  onTabChange: (tab: "all" | "cleared", via: InboxTrigger) => void;
  ordering: InboxOrdering;
  onOrderingChange: (ordering: InboxOrdering) => void;
  filter: InboxFilter | null;
  onFilterChange: (filter: InboxFilter | null, via: InboxTrigger) => void;
  unreadCount: number;
  onClearAll: (via: InboxTrigger) => void;
  /** Gates the toolbar's Clear all — false when the visible list has nothing
   *  to clear (empty, or already on the Cleared tab). */
  canClearAll: boolean;
}

/**
 * Inbox screen header. On mobile the site header rides inside the nav drawer,
 * so this header carries the trigger that opens it — the same placement every
 * page-level header uses.
 *
 * Below the title line sits the same toolbar row the table views use: the
 * filter chip on the left, and on the right the squared customize-inbox gear
 * beside a dedicated Clear all action.
 */
export function InboxHeader({
  activeTab,
  onTabChange,
  ordering,
  onOrderingChange,
  filter,
  onFilterChange,
  unreadCount,
  onClearAll,
  canClearAll,
}: InboxHeaderProps) {
  // On compact the navigation is a drawer with no rail on screen, so the
  // header carries the trigger that summons it — the other tiers' pinned
  // rail owns that job with its `>>` chip.
  const tier = useShellTier();

  // Ignore hotkeys fired while typing in a field.
  const inField = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    return (
      !!target &&
      (target.isContentEditable ||
        /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
    );
  };

  // Shift+E clears the visible tab — the page-level twin of the row's E. Bound
  // only while there is something to clear. Shift disambiguates it from the
  // hovered-row E (which ignores Shift).
  useEffect(() => {
    if (!canClearAll) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || !e.shiftKey) return;
      if (inField(e)) return;
      if (e.code === "KeyE") {
        e.preventDefault();
        onClearAll("hotkey");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canClearAll, onClearAll]);

  // Shift+A / Shift+C jump to the All / Cleared tab — the keyboard twin of
  // the segmented control, mirrored in each tab's tooltip. Same modifier
  // discipline as every Shift hotkey here: `e.code`, Shift only, never in a
  // field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || !e.shiftKey) return;
      if (inField(e)) return;
      if (e.code === "KeyA") {
        e.preventDefault();
        onTabChange("all", "hotkey");
      } else if (e.code === "KeyC") {
        e.preventDefault();
        onTabChange("cleared", "hotkey");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onTabChange]);

  // Shift+1–3 toggle the numbered filters. `e.code` (Digit1…Digit3), never
  // `e.key` — with Shift held the key is punctuation (!, @, #), so a
  // key-based gate would be a dead hotkey. A press for a filter that isn't
  // available on the current tab is ignored.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || !e.shiftKey) return;
      if (inField(e)) return;
      const def = INBOX_FILTERS.find(
        (f) => f.hotkey != null && e.code === `Digit${f.hotkey}`,
      );
      if (!def || !isInboxFilterAvailable(def.key, activeTab)) return;
      e.preventDefault();
      onFilterChange(filter === def.key ? null : def.key, "hotkey");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeTab, filter, onFilterChange]);

  return (
    <Box direction="column">
      {/* First line on the shell-wide 8px + 28px contract (see PageHeader) so
        * "Inbox" sits on the same optical line as the pane title beside it. */}
      <Box paddingX="md" paddingY="sm">
        <Box direction="row" align="center" minHeight={28}>
          <Box direction="row" align="center" gap="md">
            {tier === "compact" && <AppShellTrigger />}
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  {/* Raw string — BreadcrumbPage carries the page-title type
                      (base + semibold) and truncates; a nested default-size
                      Text would shrink it to sm on desktop and clip
                      ascenders under the outer truncate. */}
                  <BreadcrumbPage>Inbox</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <Separator orientation="vertical" className="h-5" />
            {/* All/Cleared is a binary view mode of one list, so it reads as
                a segmented control rather than page navigation. The unread
                count rides as a faint tabular numeral — metadata weight,
                never louder than the page title beside it. */}
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                onTabChange(value === "cleared" ? "cleared" : "all", "click")
              }
            >
              {/* Each tab's tooltip carries its Shift hotkey. The TooltipTrigger
                  rides INSIDE the tab's render chain (TabsTab owns a closed
                  trigger part). The active tab's tooltip is suppressed via
                  `disabled` on the ROOT, never by unmounting the popup: a
                  popup that mounts into an already-open root (the hotkey
                  flips the active tab under the cursor) positions a frame
                  after mount and slides in from the viewport corner. */}
              <TabsList size="sm">
                <Tooltip disabled={activeTab === "all"}>
                  <TabsTab value="all" render={<TooltipTrigger />}>
                    All
                    {unreadCount > 0 && (
                      <Text size={null} tabular variant="faint">
                        {unreadCount}
                      </Text>
                    )}
                  </TabsTab>
                  <TooltipPopup side="top">
                    <Box direction="row" align="center" gap="xs">
                      Go to All
                      <KbdGroup>
                        <Kbd>⇧</Kbd>
                        <Kbd>A</Kbd>
                      </KbdGroup>
                    </Box>
                  </TooltipPopup>
                </Tooltip>
                <Tooltip disabled={activeTab === "cleared"}>
                  <TabsTab value="cleared" render={<TooltipTrigger />}>
                    Cleared
                  </TabsTab>
                  <TooltipPopup side="top">
                    <Box direction="row" align="center" gap="xs">
                      Go to Cleared
                      <KbdGroup>
                        <Kbd>⇧</Kbd>
                        <Kbd>C</Kbd>
                      </KbdGroup>
                    </Box>
                  </TooltipPopup>
                </Tooltip>
              </TabsList>
            </Tabs>
          </Box>
        </Box>
      </Box>
      <Separator />

      <DataTableToolbar>
        <DataTableToolbarLeft>
          <Menu>
            {/* Applied filter reads as the workflow views' active chip:
                accent outline, the filter's own label, hover-revealed
                clear ×. */}
            <MenuTrigger
              render={
                <FilterToggle
                  size="xs"
                  icon="filter"
                  label={filter ? inboxFilterLabel(filter) : "Filter"}
                  selected={filter !== null}
                  onClear={
                    filter !== null ? () => onFilterChange(null, "click") : undefined
                  }
                  tooltip="Quickly filter notifications"
                  clearTooltip="Clear filter"
                  offVariant="outline"
                />
              }
            />
            {/* Reference menu chrome: 220px floor + an 8px gutter so the
                item highlights read as inset floating pills. */}
            <MenuPopup width="action">
              {INBOX_FILTERS.map((def) => {
                const available = isInboxFilterAvailable(def.key, activeTab);
                const shortcut =
                  def.hotkey != null ? `⇧${def.hotkey}` : undefined;
                const body = (
                  <>
                    {/* Muted glyph to the left of the label — the ref's
                        secondary icon tone, dimmer than the label ink. */}
                    <Icon name={def.icon} size="sm" color="muted" />
                    {def.label}
                    {shortcut && <MenuShortcut>{shortcut}</MenuShortcut>}
                  </>
                );

                // An unavailable filter keeps its row, disabled, and says why
                // on hover.
                return (
                  <MenuItem
                    key={def.key}
                    {...(available
                      ? { onClick: () => onFilterChange(def.key, "click") }
                      : {
                          disabled: true,
                          disabledReason:
                            "This filter is not available on this tab",
                        })}
                  >
                    {body}
                  </MenuItem>
                );
              })}
            </MenuPopup>
          </Menu>
        </DataTableToolbarLeft>
        <DataTableToolbarActions>
          <Menu>
            {/* MenuTrigger's prop set is closed, so the TooltipTrigger sits
                INSIDE its render chain — the tooltip's hover handlers merge
                down onto the button element itself. */}
            <Tooltip>
              <MenuTrigger
                render={
                  <TooltipTrigger
                    render={
                      <Button
                        variant="outline"
                        size="icon-xs"
                        shape="pill"
                        aria-label="Customize Inbox"
                      />
                    }
                  />
                }
              >
                <Icon name="settings" size="sm" />
              </MenuTrigger>
              <TooltipPopup side="top">Customize Inbox</TooltipPopup>
            </Tooltip>
            {/* Same chrome as the filter menu beside it, so the two menus in
                one header read as one object. */}
            <MenuPopup width="action">
              {/* Radio rows, so the ordering that persisted across a reload
                  is visible as the checked row. A radio row keeps its menu
                  open by default; a pick here is the whole errand, so it
                  closes the menu as the plain rows did. */}
              <MenuRadioGroup
                value={ordering}
                onValueChange={(value) =>
                  onOrderingChange(value === "oldest" ? "oldest" : "newest")
                }
              >
                <MenuRadioItem value="newest" closeOnClick>
                  <Icon color="muted" name="arrow-down-wide-narrow" size="md" />
                  Newest first
                </MenuRadioItem>
                <MenuRadioItem value="oldest" closeOnClick>
                  <Icon color="muted" name="arrow-up-wide-narrow" size="md" />
                  Oldest first
                </MenuRadioItem>
              </MenuRadioGroup>
            </MenuPopup>
          </Menu>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="outline"
                  size="xs"
                  shape="pill"
                  disabled={!canClearAll}
                  onClick={() => onClearAll("click")}
                />
              }
            >
              <Icon name="check-check" size="sm" />
              <Text size={null}>Clear all</Text>
            </TooltipTrigger>
            <TooltipPopup side="top">
              <Box direction="row" align="center" gap="sm">
                Clear this tab
                <KbdGroup>
                  <Kbd>⇧</Kbd>
                  <Kbd>E</Kbd>
                </KbdGroup>
              </Box>
            </TooltipPopup>
          </Tooltip>
        </DataTableToolbarActions>
      </DataTableToolbar>
    </Box>
  );
}
